import React, { useState, useEffect } from 'react';
import {
  Container, Card, Form, Button, InputGroup, Spinner, Alert, Modal, Badge
} from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import {
  getLoyaltyCardByPhone,
  createLoyaltyCard,
  addStamp,
  deleteLoyaltyCard,
  getAllLoyaltyCards
} from '../services/loyaltyCardApi';
import { getCustomers } from '../services/customerApi';
import { useAuth } from '../context/AuthContext';
import './LoyaltyCard.css';

const LoyaltyCard = () => {
  const { user } = useAuth();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [allCards, setAllCards] = useState([]);
  const [showAllCards, setShowAllCards] = useState(false);

  const STAMPS_REQUIRED = 10;

  // Buscar cliente y tarjeta por teléfono
  const handleSearch = async () => {
    if (!phone.trim()) {
      setError('Por favor ingresa un número de teléfono');
      return;
    }

    setLoading(true);
    setError('');
    setCard(null);
    setCustomer(null);

    try {
      // Buscar cliente por teléfono
      const customerRes = await getCustomers({ q: phone, per_page: 10 });
      
      if (customerRes.ok && customerRes.data?.data?.length > 0) {
        // Buscar en todos los campos de teléfono posibles
        const foundCustomer = customerRes.data.data.find(
          c => c.phone_whatsapp === phone || 
               c.person?.phone === phone || 
               c.person?.cellphone === phone ||
               c.phone_whatsapp?.includes(phone) ||
               phone.includes(c.phone_whatsapp)
        ) || customerRes.data.data[0]; // Si no encuentra exacto, usa el primero
        
        if (foundCustomer) {
          setCustomer(foundCustomer);
          
          // Buscar tarjeta de fidelización
          const cardRes = await getLoyaltyCardByPhone(foundCustomer.phone_whatsapp || phone);
          console.log('Card response:', cardRes);
          if (cardRes.ok && cardRes.data) {
            // La respuesta puede ser un objeto directo o estar en data
            const cardData = cardRes.data.data || cardRes.data;
            // Si es un array, tomar el primero
            const finalCard = Array.isArray(cardData) ? cardData[0] : cardData;
            if (finalCard && (finalCard.loyalty_card_id || finalCard.id_loyalty_card)) {
              // Normalizar campos
              finalCard.id_loyalty_card = finalCard.loyalty_card_id || finalCard.id_loyalty_card;
              finalCard.current_stamps = finalCard.total_stamp || finalCard.current_stamps || 0;
              setCard(finalCard);
            }
          }
        } else {
          setError('No se encontró un cliente con ese número de teléfono');
        }
      } else {
        setError('No se encontró un cliente con ese número de teléfono');
      }
    } catch (err) {
      setError('Error al buscar. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Crear tarjeta nueva
  const handleCreateCard = async () => {
    if (!customer) return;
    
    setLoading(true);
    setError('');

    try {
      const clientId = customer.id_client || customer.id;
      console.log('Creating card for client:', clientId, customer);
      const res = await createLoyaltyCard(clientId, STAMPS_REQUIRED);
      if (res.ok) {
        setCard(res.data.data || { current_stamps: 0, stamps_required: STAMPS_REQUIRED });
        setSuccess('¡Tarjeta de fidelización creada exitosamente!');
        setTimeout(() => setSuccess(''), 3000);
        // Recargar datos
        handleSearch();
      } else {
        setError(res.data?.message || 'Error al crear la tarjeta');
      }
    } catch (err) {
      setError('Error al crear la tarjeta');
    } finally {
      setLoading(false);
    }
  };

  // Agregar sello
  const handleAddStamp = async () => {
    if (!card) return;

    setLoading(true);
    setError('');

    try {
      const cardId = card.id_loyalty_card || card.id;
      console.log('Adding stamp to card:', cardId, card);
      const res = await addStamp(cardId, user?.id_user);
      if (res.ok) {
        const newStamps = (card.current_stamps || 0) + 1;
        const updatedCard = { ...card, current_stamps: newStamps };
        setCard(updatedCard);
        
        // Verificar si completó la tarjeta
        if (newStamps >= (card.stamps_required || STAMPS_REQUIRED)) {
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 5000);
        } else {
          setSuccess('¡Sello agregado!');
          setTimeout(() => setSuccess(''), 2000);
        }
      } else {
        setError(res.data?.message || 'Error al agregar sello');
      }
    } catch (err) {
      setError('Error al agregar sello');
    } finally {
      setLoading(false);
    }
  };

  // Eliminar tarjeta
  const handleDeleteCard = async () => {
    if (!card) return;

    setLoading(true);
    try {
      const res = await deleteLoyaltyCard(card.id_loyalty_card);
      if (res.ok) {
        setCard(null);
        setSuccess('Tarjeta eliminada');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Error al eliminar la tarjeta');
      }
    } catch (err) {
      setError('Error al eliminar la tarjeta');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Cargar todas las tarjetas
  const loadAllCards = async () => {
    setLoading(true);
    try {
      const res = await getAllLoyaltyCards();
      if (res.ok) {
        setAllCards(res.data?.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Generar confetti
  const generateConfetti = () => {
    const colors = ['#ffd700', '#ff6b6b', '#00d9ff', '#00ff88', '#e94560'];
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100 + '%',
      delay: Math.random() * 2 + 's',
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
  };

  const currentStamps = card?.current_stamps || 0;
  const stampsRequired = card?.stamps_required || STAMPS_REQUIRED;

  return (
    <>
      <Helmet>
        <title>Tarjeta de Fidelización - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="loyalty-container">
        <Container>
          <div className="loyalty-card-wrapper">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="loyalty-main-card">
                {/* Header */}
                <div className="loyalty-header">
                  <h3>🐾 Tarjeta de Fidelización</h3>
                  <p>¡Acumula sellos y gana premios!</p>
                </div>

                {/* Búsqueda */}
                <div className="search-section">
                  <InputGroup className="search-input-group">
                    <Form.Control
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Ingresa el número de celular..."
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button 
                      className="search-btn"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : <i className="fas fa-search"></i>}
                    </Button>
                  </InputGroup>
                </div>

                {/* Alertas */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Alert variant="danger" className="m-3 mb-0" onClose={() => setError('')} dismissible>
                        {error}
                      </Alert>
                    </motion.div>
                  )}
                  {success && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Alert variant="success" className="m-3 mb-0">
                        {success}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Contenido principal */}
                <div className="stamp-card-section">
                  {customer && !card && (
                    // Cliente encontrado pero sin tarjeta
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="empty-state"
                    >
                      <div className="empty-icon">🎴</div>
                      <h4>{customer.name || customer.person?.first_name} {customer.surname || customer.person?.last_name}</h4>
                      <p>Este cliente aún no tiene tarjeta de fidelización</p>
                      <Button 
                        className="btn-create-card"
                        onClick={handleCreateCard}
                        disabled={loading}
                      >
                        {loading ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-plus me-2"></i>}
                        Crear Tarjeta
                      </Button>
                    </motion.div>
                  )}

                  {customer && card && (
                    // Mostrar tarjeta con sellos
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="customer-info">
                        <h4>{customer.name || customer.person?.first_name} {customer.surname || customer.person?.last_name}</h4>
                        <p><i className="fas fa-phone me-1"></i> {customer.phone_whatsapp || phone}</p>
                      </div>

                      {/* Grid de sellos */}
                      <div className="stamps-grid">
                        {Array.from({ length: stampsRequired }, (_, i) => (
                          <motion.div
                            key={i}
                            className={`stamp-slot ${i < currentStamps ? 'filled' : 'empty'} ${i === stampsRequired - 1 ? 'reward' : ''}`}
                            initial={i < currentStamps ? { scale: 0 } : {}}
                            animate={{ scale: 1 }}
                            transition={{ delay: i * 0.05 }}
                          >
                            {i < currentStamps ? (
                              <span>🐾</span>
                            ) : i === stampsRequired - 1 ? (
                              <span>🎁</span>
                            ) : (
                              <span style={{ opacity: 0.3 }}>{i + 1}</span>
                            )}
                          </motion.div>
                        ))}
                      </div>

                      {/* Progreso */}
                      <div className="progress-section">
                        <p className="progress-text">
                          <strong>{currentStamps}</strong> de <strong>{stampsRequired}</strong> sellos
                        </p>
                        <div className="progress-bar-custom">
                          <motion.div
                            className="progress-fill"
                            initial={{ width: 0 }}
                            animate={{ width: `${(currentStamps / stampsRequired) * 100}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>

                      {/* Botones */}
                      <div className="action-buttons">
                        <Button
                          className="btn-add-stamp"
                          onClick={handleAddStamp}
                          disabled={loading || currentStamps >= stampsRequired}
                        >
                          {loading ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-stamp me-2"></i>}
                          {currentStamps >= stampsRequired ? '¡Tarjeta Completa!' : 'Agregar Sello'}
                        </Button>
                        <Button
                          className="btn-delete-card"
                          onClick={() => setShowDeleteModal(true)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {!customer && !loading && (
                    <div className="empty-state">
                      <div className="empty-icon">📱</div>
                      <h4>Busca un cliente</h4>
                      <p>Ingresa el número de celular para buscar o crear una tarjeta de fidelización</p>
                    </div>
                  )}
                </div>

                {/* Ver todas las tarjetas */}
                <div className="p-3 border-top" style={{ borderColor: '#eee' }}>
                  <Button
                    variant="link"
                    className="text-dark w-100"
                    onClick={() => {
                      setShowAllCards(!showAllCards);
                      if (!showAllCards) loadAllCards();
                    }}
                  >
                    <i className={`fas fa-chevron-${showAllCards ? 'up' : 'down'} me-2`}></i>
                    {showAllCards ? 'Ocultar' : 'Ver todas las tarjetas'}
                  </Button>

                  <AnimatePresence>
                    {showAllCards && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="cards-list-section"
                      >
                        {allCards.map((c) => (
                          <div key={c.id_loyalty_card} className="card-list-item">
                            <div className="card-list-info">
                              <h6>{c.client_name || 'Cliente'}</h6>
                              <small>{c.phone || 'Sin teléfono'}</small>
                            </div>
                            <div className="card-list-stamps">
                              <Badge bg={c.current_stamps >= c.stamps_required ? 'success' : 'secondary'}>
                                {c.current_stamps}/{c.stamps_required}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {allCards.length === 0 && !loading && (
                          <p className="text-center text-muted mt-3">No hay tarjetas registradas</p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Card>
            </motion.div>
          </div>
        </Container>
      </div>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar Tarjeta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas eliminar esta tarjeta de fidelización?</p>
          <p className="text-danger"><strong>Esta acción no se puede deshacer.</strong></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteCard} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Celebración */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            className="celebration-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCelebration(false)}
          >
            {generateConfetti().map((confetti) => (
              <div
                key={confetti.id}
                className="confetti-piece"
                style={{
                  left: confetti.left,
                  animationDelay: confetti.delay,
                  backgroundColor: confetti.color,
                  borderRadius: Math.random() > 0.5 ? '50%' : '0'
                }}
              />
            ))}
            <motion.div
              className="celebration-content"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10 }}
            >
              <div className="celebration-icon">🎉</div>
              <h2>¡FELICITACIONES!</h2>
              <p>Has completado tu tarjeta de fidelización</p>
              <p>🎁 ¡Reclama tu premio!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LoyaltyCard;

